// src/screens/HouseholdAssetsExpress/HouseholdAssetsExpress.tsx
// -----------------------------------------------------------------------------
// Container component: pulls household data, derives tasks, renders progress
// bar, list, and context-menu.
// -----------------------------------------------------------------------------

import type { FC } from "react";
import { useCallback, useMemo, useState, useEffect } from "react";

import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";

import useHousehold from "src/contexts/household-context";
import type {
  Task,
  Household,
  UserDataObj,
  Asset as BackendAsset,
  AssetType,
  FinancialAccountTypes,
  RealEstateTypes,
  PersonalPropertyTypes,
  InsurancePolicyTypes,
  BusinessType,
} from "src/types/snugtotal";
import { paths } from "src/paths";
import { useAssetsStore } from "src/hooks/snug/use-assets";
import { toast } from "react-hot-toast";
import CircularProgress from "@mui/material/CircularProgress";
import useUserDataProvider from "src/contexts/userData-context";
import Avatar from "@mui/material/Avatar";
import AvatarGroup from "@mui/material/AvatarGroup";
import IconButton from "@mui/material/IconButton";
import Chip from "@mui/material/Chip";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import DirectionsCarOutlinedIcon from "@mui/icons-material/DirectionsCarOutlined";
import SavingsOutlinedIcon from "@mui/icons-material/SavingsOutlined";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BeachAccessOutlinedIcon from "@mui/icons-material/BeachAccessOutlined";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import EqualizerOutlinedIcon from "@mui/icons-material/EqualizerOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from "recharts";

/* =============================================================================
   Inline helpers
============================================================================= */

/** narrow filter: remove null | undefined */
const notEmpty = <T,>(v: T | null | undefined): v is T => v != null;
/** docs -> has at least one with status === APPROVED */
const hasApproved = (docs?: any[]) =>
  docs?.some(
    (d) =>
      d?.status === "APPROVED" ||
      d?.status === "ANALYSIS_PENDING" ||
      d?.status === "ANALYSIS_COMPLETE"
  );

// Helper function to generate avatar colors and initials
function stringToColor(string: string) {
  let hash = 0;
  let i;

  /* eslint-disable no-bitwise */
  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate RGB values with constraints for professional colors
  const r = (hash >> 16) & 0xff;
  const g = (hash >> 8) & 0xff;
  const b = hash & 0xff;

  // Mix with gray to reduce saturation (70% original color, 30% gray)
  const gray = 128;
  const mixedR = Math.floor(r * 1 + gray * 0);
  const mixedG = Math.floor(g * 1 + gray * 0);
  const mixedB = Math.floor(b * 1 + gray * 0);

  // Constrain to professional range (60-180) to avoid extremes
  const constrainedR = Math.min(180, Math.max(60, mixedR));
  const constrainedG = Math.min(180, Math.max(60, mixedG));
  const constrainedB = Math.min(180, Math.max(60, mixedB));

  /* eslint-enable no-bitwise */

  // Convert to hex
  const toHex = (n: number) => n.toString(16).padStart(2, "0");
  return `#${toHex(constrainedR)}${toHex(constrainedG)}${toHex(constrainedB)}`;
}

function stringAvatar(name: string | null) {
  if (!name) {
    return {
      sx: {
        bgcolor: "#FFD700",
        color: "black",
        height: 40,
        width: 40,
      },
      children: "-",
    };
  }

  const initials_array = name.split(" ");

  if (initials_array.length === 1) {
    return {
      sx: {
        bgcolor: stringToColor(name),
        color: "white",
        height: 40,
        width: 40,
      },
      children: `${name[0]}${name[1]}`,
    };
  } else {
    return {
      sx: {
        bgcolor: stringToColor(name),
        color: "white",
        height: 40,
        width: 40,
      },
      children: `${initials_array[0][0]}${initials_array[1][0]}`,
    };
  }
}

// ---------------- helpers ----------------------------------------------------
type PersonRole = "HOH" | "SPOUSE" | "CHILD" | "OTHER";
type TaskPlusRole = Task & { primaryRole: PersonRole };

// Asset type definitions
type AssetCategory =
  | "real_estate"
  | "vehicle"
  | "bank_account"
  | "investment"
  | "retirement"
  | "business"
  | "life_insurance";

interface Asset {
  id: string;
  title: string;
  value: number;
  category: AssetCategory;
  status: "included" | "excluded" | "pending";
}

// Mapping functions for express <-> backend asset conversion
// ============================================================================

/**
 * Maps an express category to the corresponding backend asset type and subtypes
 */
const getCategoryToAssetTypeMapping = (
  category: AssetCategory
): {
  asset_type: AssetType;
  account_subtype?: FinancialAccountTypes;
  property_type?: RealEstateTypes;
  personal_property_type?: PersonalPropertyTypes;
  insurance_type?: InsurancePolicyTypes;
  business_type?: BusinessType;
} => {
  switch (category) {
    case "bank_account":
      return {
        asset_type: "financial_account",
        account_subtype: "CHECKING",
      };
    case "investment":
      return {
        asset_type: "financial_account",
        account_subtype: "INVESTMENT",
      };
    case "retirement":
      return {
        asset_type: "financial_account",
        account_subtype: "RETIREMENT",
      };
    case "real_estate":
      return {
        asset_type: "realestate",
        property_type: undefined,
      };
    case "vehicle":
      return {
        asset_type: "personal_property",
        personal_property_type: "VEHICLES",
      };
    case "business":
      return {
        asset_type: "business_interest",
        business_type: undefined,
      };
    case "life_insurance":
      return {
        asset_type: "insurance_asset",
        insurance_type: undefined,
      };
    default:
      return {
        asset_type: "financial_account",
        account_subtype: "CHECKING",
      };
  }
};

/**
 * Maps a backend Asset to an express Asset
 */
const mapBackendAssetToExpress = (backendAsset: BackendAsset): Asset => {
  // Determine express category from backend asset_type and subtypes
  let category: AssetCategory = "bank_account";

  if (backendAsset.asset_type === "financial_account") {
    switch (backendAsset.account_subtype) {
      case "CHECKING":
      case "SAVINGS":
        category = "bank_account";
        break;
      case "INVESTMENT":
        category = "investment";
        break;
      case "RETIREMENT":
        category = "retirement";
        break;
      default:
        category = "bank_account";
    }
  } else if (backendAsset.asset_type === "realestate") {
    category = "real_estate";
  } else if (backendAsset.asset_type === "personal_property") {
    category = "vehicle";
  } else if (backendAsset.asset_type === "business_interest") {
    category = "business";
  } else if (backendAsset.asset_type === "insurance_asset") {
    category = "life_insurance";
  }

  // Get title from institution or other relevant field
  let title = "";
  if (backendAsset.institution) {
    title = backendAsset.institution;
  } else if (backendAsset.address) {
    title = backendAsset.address;
  } else if (backendAsset.business_name) {
    title = backendAsset.business_name;
  } else if (backendAsset.coverage_institution) {
    title = backendAsset.coverage_institution;
  } else if (
    backendAsset.notes &&
    backendAsset.asset_type === "personal_property"
  ) {
    title = backendAsset.notes;
  } else {
    title = "Unnamed Asset";
  }

  return {
    id: backendAsset.id || "",
    title,
    value: backendAsset.value || 0,
    category,
    status: "included",
  };
};

/**
 * Maps an express Asset to a backend Asset structure for API calls
 */
const mapExpressAssetToBackend = (
  expressAsset: Asset,
  ud_id: string
): BackendAsset => {
  const mapping = getCategoryToAssetTypeMapping(expressAsset.category);

  // Build base asset object
  const backendAsset: BackendAsset = {
    id: expressAsset.id || null,
    asset_type: mapping.asset_type,
    value: expressAsset.value,
    notes: "",
    documents: [],
  };

  // Add type-specific fields
  if (mapping.account_subtype) {
    backendAsset.account_subtype = mapping.account_subtype;
    backendAsset.institution = expressAsset.title;
  }

  if ("property_type" in mapping) {
    backendAsset.property_type = mapping.property_type;
    backendAsset.address = expressAsset.title;
  }

  if (mapping.personal_property_type) {
    backendAsset.personal_property_type = mapping.personal_property_type;
    backendAsset.notes = expressAsset.title;
  }

  if ("insurance_type" in mapping) {
    backendAsset.insurance_type = mapping.insurance_type;
    backendAsset.coverage_institution = expressAsset.title;
  }

  if ("business_type" in mapping) {
    backendAsset.business_type = mapping.business_type;
    backendAsset.business_name = expressAsset.title;
  }

  return backendAsset;
};

// Helper function to generate default asset name based on category
const getDefaultAssetName = (
  category: AssetCategory,
  existingAssets: Asset[]
): string => {
  const categoryNames: Record<AssetCategory, string> = {
    real_estate: "Real Estate",
    vehicle: "Vehicle",
    bank_account: "Bank Account",
    investment: "Investment Account",
    retirement: "Retirement Account",
    business: "Business",
    life_insurance: "Life Insurance Policy",
  };

  const baseName = categoryNames[category];
  const existingOfCategory = existingAssets.filter(
    (a) => a.category === category
  );
  const nextNumber = existingOfCategory.length + 1;

  return `${baseName} #${nextNumber}`;
};

// Helper function to get category colors (dot, background, and dark text)
const getCategoryColors = (
  category: AssetCategory
): { dotColor: string; bgColor: string; darkColor: string } => {
  const colors: Record<
    AssetCategory,
    { dotColor: string; bgColor: string; darkColor: string }
  > = {
    real_estate: {
      dotColor: "#4caf50",
      bgColor: "#e8f5e9",
      darkColor: "#2e7d32",
    }, // Green
    bank_account: {
      dotColor: "#2196f3",
      bgColor: "#e3f2fd",
      darkColor: "#1565c0",
    }, // Blue
    investment: {
      dotColor: "#9c27b0",
      bgColor: "#f3e5f5",
      darkColor: "#6a1b9a",
    }, // Purple
    retirement: {
      dotColor: "#ff9800",
      bgColor: "#fff3e0",
      darkColor: "#e65100",
    }, // Orange
    vehicle: { dotColor: "#00bcd4", bgColor: "#e0f7fa", darkColor: "#00838f" }, // Cyan
    business: { dotColor: "#795548", bgColor: "#efebe9", darkColor: "#4e342e" }, // Brown
    life_insurance: {
      dotColor: "#e91e63",
      bgColor: "#fce4ec",
      darkColor: "#ad1457",
    }, // Pink
  };

  return (
    colors[category] || {
      dotColor: "#9e9e9e",
      bgColor: "#f5f5f5",
      darkColor: "#616161",
    }
  );
};

// Helper function to get asset routing based on planning mode and asset type
type RoutingMethod = "direct" | "trust" | "probate";
type RoutingTiming = "immediate" | "months_1_3" | "months_12_18";

const getAssetRouting = (
  asset: Asset,
  planningMode: string
): { method: RoutingMethod; timing: RoutingTiming } => {
  if (planningMode === "no-plan") {
    // No plan: Life insurance, retirement, and vehicles can transfer directly
    if (
      asset.category === "life_insurance" ||
      asset.category === "retirement" ||
      asset.category === "vehicle"
    ) {
      return { method: "direct", timing: "immediate" };
    }
    // Everything else goes through probate without planning
    return { method: "probate", timing: "months_12_18" };
  } else if (planningMode === "will-plan") {
    // Will plan: Life insurance, retirement, and vehicles are direct
    if (
      asset.category === "life_insurance" ||
      asset.category === "retirement" ||
      asset.category === "vehicle"
    ) {
      return { method: "direct", timing: "immediate" };
    }
    // Bank accounts, investments, real estate, and business go through probate
    return { method: "probate", timing: "months_12_18" };
  } else if (planningMode === "trust-plan") {
    // Trust plan: Life insurance, retirement, and vehicles are always direct
    if (
      asset.category === "life_insurance" ||
      asset.category === "retirement" ||
      asset.category === "vehicle"
    ) {
      return { method: "direct", timing: "immediate" };
    }
    // Bank accounts, investments, real estate, and business go through trust
    return { method: "trust", timing: "months_1_3" };
  }
  return { method: "probate", timing: "months_12_18" };
};

// Calculate total value of all assets
const calculateTotalValue = (
  assets: Asset[],
  assetValues: Record<string, number>
): number => {
  return assets.reduce((total, asset) => {
    const value =
      assetValues[asset.id] !== undefined ? assetValues[asset.id] : asset.value;
    return total + value;
  }, 0);
};

// Get breakdown of assets by routing method
const getRoutingBreakdown = (
  assets: Asset[],
  assetValues: Record<string, number>,
  planningMode: string
): { direct: number; trust: number; probate: number } => {
  const breakdown = { direct: 0, trust: 0, probate: 0 };

  assets.forEach((asset) => {
    const value =
      assetValues[asset.id] !== undefined ? assetValues[asset.id] : asset.value;
    const routing = getAssetRouting(asset, planningMode);
    breakdown[routing.method] += value;
  });

  return breakdown;
};

// Helper function to get assets grouped by their routing method
const getAssetsByRouting = (
  assets: Asset[],
  assetValues: Record<string, number>,
  assetTitles: Record<string, string>,
  planningMode: string
): { direct: Asset[]; trust: Asset[]; probate: Asset[] } => {
  const grouped = {
    direct: [] as Asset[],
    trust: [] as Asset[],
    probate: [] as Asset[],
  };

  assets.forEach((asset) => {
    const routing = getAssetRouting(asset, planningMode);
    grouped[routing.method].push(asset);
  });

  return grouped;
};

// Helper function to format large dollar amounts
const formatCurrency = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value.toFixed(0)}`;
  }
};

// Calculate timeline data based on actual assets (returns dollar values, not percentages)
const calculateTimelineData = (
  assets: Asset[],
  assetValues: Record<string, number>,
  planningMode: string
) => {
  const total = calculateTotalValue(assets, assetValues);

  // Generate timeline data points
  const data: Array<{ month: number; [key: string]: number }> = [
    { month: 0, "No Plan": 0, "Will Plan": 0, "Trust Plan": 0 },
    { month: 1, "No Plan": 0, "Will Plan": 0, "Trust Plan": 0 },
    { month: 3, "No Plan": 0, "Will Plan": 0, "Trust Plan": 0 },
    { month: 6, "No Plan": 0, "Will Plan": 0, "Trust Plan": 0 },
    { month: 9, "No Plan": 0, "Will Plan": 0, "Trust Plan": 0 },
    { month: 12, "No Plan": 0, "Will Plan": 0, "Trust Plan": 0 },
    { month: 15, "No Plan": 0, "Will Plan": 0, "Trust Plan": 0 },
    { month: 18, "No Plan": 0, "Will Plan": 0, "Trust Plan": 0 },
    { month: 21, "No Plan": 0, "Will Plan": 0, "Trust Plan": 0 },
  ];

  // Calculate for each planning mode
  const modes = {
    "no-plan": "No Plan",
    "will-plan": "Will Plan",
    "trust-plan": "Trust Plan",
  };

  Object.entries(modes).forEach(([mode, label]) => {
    const modeBreakdown = getRoutingBreakdown(assets, assetValues, mode);
    const modeImmediate = modeBreakdown.direct;
    const mode1to3Total = modeBreakdown.direct + modeBreakdown.trust;

    data.forEach((point) => {
      if (point.month === 0) {
        point[label] = Math.round(modeImmediate);
      } else if (point.month <= 3) {
        if (mode === "no-plan" || mode === "will-plan") {
          // No Plan and Will Plan: Stay at immediate value (no probate yet)
          point[label] = Math.round(modeImmediate);
        } else if (mode === "trust-plan") {
          // Trust Plan: Linear progression from immediate to 1-3 month total
          point[label] = Math.round(
            modeImmediate + (mode1to3Total - modeImmediate) * (point.month / 3)
          );
        }
      } else if (point.month < 9) {
        if (mode === "no-plan" || mode === "will-plan") {
          // No Plan and Will Plan: Stay flat at immediate value (probate not started)
          point[label] = Math.round(modeImmediate);
        } else if (mode === "trust-plan") {
          // Trust Plan: Most assets delivered by month 3, slow trickle after
          point[label] = Math.round(
            mode1to3Total + (total - mode1to3Total) * ((point.month - 3) / 18)
          );
        }
      } else if (point.month < 12) {
        if (mode === "no-plan") {
          // No Plan: Still waiting, probate not started until month 12
          point[label] = Math.round(modeImmediate);
        } else if (mode === "will-plan") {
          // Will Plan: Probate starts at month 9, completes by month 15
          point[label] = Math.round(
            modeImmediate + (total - modeImmediate) * ((point.month - 9) / 6)
          );
        } else if (mode === "trust-plan") {
          // Trust Plan: Continue slow completion
          point[label] = Math.round(
            mode1to3Total + (total - mode1to3Total) * ((point.month - 3) / 18)
          );
        }
      } else if (point.month <= 21) {
        if (mode === "no-plan") {
          // No Plan: Probate starts at month 12, completes by month 21
          point[label] = Math.round(
            modeImmediate + (total - modeImmediate) * ((point.month - 12) / 9)
          );
        } else if (mode === "will-plan") {
          // Will Plan: Complete by month 15
          if (point.month <= 15) {
            point[label] = Math.round(
              modeImmediate + (total - modeImmediate) * ((point.month - 9) / 6)
            );
          } else {
            point[label] = Math.round(total);
          }
        } else if (mode === "trust-plan") {
          // Trust Plan: Should be complete by now
          point[label] = Math.round(total);
        }
      }
    });
  });

  return data;
};

const roleOf = (
  user: UserDataObj,
  hoh: UserDataObj,
  spouse?: UserDataObj | null
): PersonRole => {
  if (user.ud_id === hoh.ud_id) return "HOH";
  if (spouse && user.ud_id === spouse.ud_id) return "SPOUSE";
  // if your member object carries a role field you can refine this:
  return "CHILD"; // or 'OTHER'
};

// ---------------- buildTasks -------------------------------------------------
const buildTasks = (
  hh?: Household | null,
  userData?: UserDataObj | null
): TaskPlusRole[] => {
  if (!hh || !hh.head_of_household) return [];

  const hoh = hh.head_of_household;
  const members = hh.household_member_roles ?? [];
  const spouse =
    members.find((m) => m.role === "SPOUSE")?.role_target_user_data ?? null;

  const epf = hoh.estate_plan_foundation as "joint_trust" | "single_trust" | "two_trusts" | "single_will" | "two_wills" | "attorney" | null | "";

  const tasks: TaskPlusRole[] = [];

  // ---- 1. Will / Trust rows -----------------------------------------------
  if (epf === "joint_trust") {
    tasks.push({
      id: "jointTrust",
      kind: "lwt",
      title: "Joint Revocable Living Trust",
      people: [hoh, spouse].filter(notEmpty),
      estMinutes: 18,
      status: hasApproved(hoh.wills_and_trusts) ? "COMPLETE" : "NOT_STARTED",
      updatedAt: hoh.wills_and_trusts?.[0]?.updated_at,
      path: paths.dashboard.lwt(hoh.ud_id!),
      primaryRole: "HOH", // 👈 added
      estateDocument: hoh.wills_and_trusts?.[0],
      ud_id: hoh.ud_id ?? "",
      type: "TRUST",
      joint_trust: true,
      filteredEstateDocs: hoh.wills_and_trusts ?? [],
    });
  } else if (epf === "" || epf === null) {
    tasks.push({
      id: "skip",
      kind: "lwt",
      title: "Choose a Will or Trust",
      people: [hoh, spouse].filter(notEmpty),
      estMinutes: 5,
      status: "NOT_STARTED",
      updatedAt: hoh.wills_and_trusts?.[0]?.updated_at,
      path: paths.dashboard.lwt(userData?.ud_id!),
      primaryRole: "HOH", // 👈 added
      estateDocument: hoh.wills_and_trusts?.[0],
      ud_id: hoh.ud_id ?? "",
      type: "WILL",
      joint_trust: false,
      filteredEstateDocs: hoh.wills_and_trusts ?? [],
    });
  } else if (epf === "attorney") {
    tasks.push({
      id: "attorney",
      kind: "lwt",
      title: "Upload Will or Trust from Attorney",
      people: [hoh, spouse].filter(notEmpty),
      estMinutes: 2,
      status: hasApproved(hoh.wills_and_trusts) ? "COMPLETE" : "NOT_STARTED",
      updatedAt: hoh.wills_and_trusts?.[0]?.updated_at,
      path: paths.dashboard.lwt(hoh.ud_id!),
      primaryRole: "HOH", // 👈 added
      estateDocument: hoh.wills_and_trusts?.[0],
      ud_id: hoh.ud_id ?? "",
      type: "WILL",
      joint_trust: false,
      filteredEstateDocs: hoh.wills_and_trusts ?? [],
    });
  } else if (epf === "single_trust" || epf === "two_trusts" || epf === "single_will" || epf === "two_wills") {
    const label = (epf === "single_trust" || epf === "two_trusts") ? "Living Trust" : "Last Will & Testament";

    [hoh, spouse].filter(notEmpty).forEach((p) => {
      const docs = p.wills_and_trusts ?? [];
      const first = p.full_name?.split(/\s+/)[0] ?? "Member";

      tasks.push({
        id: `${p.ud_id}-lwt`,
        kind: "lwt",
        title: `${label} for ${first}`,
        type: label === "Living Trust" ? "TRUST" : "WILL",
        people: [p],
        estMinutes: (epf === "single_trust" || epf === "two_trusts") ? 10 : 8,
        status: hasApproved(docs) ? "COMPLETE" : "NOT_STARTED",
        updatedAt: docs[0]?.updated_at,
        path: paths.dashboard.lwt(p.ud_id!),
        primaryRole: roleOf(p, hoh, spouse), // 👈 added
        estateDocument: docs[0],
        ud_id: p.ud_id ?? "",
        joint_trust: false,
        filteredEstateDocs: docs,
      });
    });
  }

  // ---- 2. POA + HCD rows for everyone -------------------------------------
  const everyone: UserDataObj[] = [
    hoh,
    ...members
      .filter((r) => r.is_read_only !== true)
      .map((r) => r.role_target_user_data),
  ].filter(notEmpty);

  everyone.forEach((p) => {
    const first = p.full_name?.split(/\s+/)[0] ?? "Member";
    const r = roleOf(p, hoh, spouse);

    // POA
    tasks.push({
      id: `${p.ud_id}-fpoa`,
      kind: "fpoa",
      title: `Power of Attorney for ${first}`,
      type: "FINANCIAL_POWER_OF_ATTORNEY",
      people: [p],
      estMinutes: 3,
      status: hasApproved(p.financial_powers_of_attorney)
        ? "COMPLETE"
        : "NOT_STARTED",
      updatedAt: p.financial_powers_of_attorney?.[0]?.updated_at,
      path: paths.dashboard.fpoa(p.ud_id!),
      primaryRole: r, // 👈 added
      estateDocument: p.financial_powers_of_attorney?.[0],
      ud_id: p.ud_id ?? "",
      joint_trust: false,
      filteredEstateDocs: p.financial_powers_of_attorney ?? [],
    });

    // HCD
    tasks.push({
      id: `${p.ud_id}-hcd`,
      kind: "hcd",
      title: `Health Care Directive for ${first}`,
      type: "HEALTHCARE_DIRECTIVE",
      people: [p],
      estMinutes: 2,
      status: hasApproved(p.healthcare_directives) ? "COMPLETE" : "NOT_STARTED",
      updatedAt: p.healthcare_directives?.[0]?.updated_at,
      path: paths.dashboard.hcd(p.ud_id!),
      primaryRole: r, // 👈 added
      estateDocument: p.healthcare_directives?.[0],
      ud_id: p.ud_id ?? "",
      joint_trust: false,
      filteredEstateDocs: p.healthcare_directives ?? [],
    });
  });

  return tasks;
};

/* ---------------------------------------------------------------------------
   Sort tasks by (1) person-role priority, then (2) completion status, then
   (3) kind ('lwt', 'fpoa', 'hcd') for a stable, readable list.
--------------------------------------------------------------------------- */

const rolePriority: Record<PersonRole, number> = {
  HOH: 0,
  SPOUSE: 1,
  CHILD: 2,
  OTHER: 3,
};

const statusPriority: Record<Task["status"], number> = {
  NOT_STARTED: 0,
  IN_PROGRESS: 0,
  COMPLETE: 0,
  PENDING: 0,
  REVIEWABLE: 0,
};

const kindOrder: Record<Task["kind"], number> = {
  // tie-breaker
  lwt: 0,
  fpoa: 1,
  hcd: 2,
  assets: 3,
};

const epfToPlanningMode = (epf: string | null | undefined): string => {
  switch (epf) {
    case "joint_trust":
    case "single_trust":
    case "two_trusts":
    case "attorney":
      return "trust-plan";
    case "single_will":
    case "two_wills":
      return "will-plan";
    default:
      return "no-plan";
  }
};

const sortTasks = (tasks: TaskPlusRole[]): TaskPlusRole[] =>
  [...tasks].sort((a, b) => {
    // 1️⃣  status: completed items always last
    const byStatus = statusPriority[a.status] - statusPriority[b.status]; // 0,1,2
    if (byStatus !== 0) return byStatus;

    // 2️⃣  same status → sort by household role
    const byRole = rolePriority[a.primaryRole] - rolePriority[b.primaryRole];
    if (byRole !== 0) return byRole;

    // 3️⃣  tie-breaker: document kind
    return kindOrder[a.kind] - kindOrder[b.kind];
  });

const HouseholdAssetsExpress: FC<{
  readOnly: boolean;
  express: boolean;
  triggerConfetti?: (source?: "checklist" | "download") => void;
  setViewMode?: (mode: "intake" | "download") => void;
  personUdId?: string | null;  // Allow overriding which person's assets to show
}> = ({ readOnly, express, triggerConfetti, setViewMode, personUdId }) => {
  // ---------------------------------------------------------------------------
  // 1. source data → flat task array
  // ---------------------------------------------------------------------------
  const {
    selectedHousehold,
  } = useHousehold();
  const { userData } = useUserDataProvider();

  // Use personUdId prop if provided, otherwise fall back to logged-in user
  const effectiveUdId = personUdId || userData?.ud_id || null;

  const epf = selectedHousehold?.head_of_household?.estate_plan_foundation;

  const tasks = useMemo(
    () => sortTasks(buildTasks(selectedHousehold, userData)),
    [selectedHousehold, userData]
  );

  // Assets store hook for backend integration
  const {
    assets: backendAssets,
    handleAddOrUpdateAssets,
    handleDeleteAssets,
  } = useAssetsStore(effectiveUdId);

  // State for selected documents
  const [selectedDocs, setSelectedDocs] = useState<Record<string, boolean>>({});

  // Memoized assets from backend
  const assets = useMemo(() => {
    return backendAssets
      .filter(
        (ba) =>
          ba.asset_type === "financial_account" ||
          ba.asset_type === "realestate" ||
          ba.asset_type === "personal_property" ||
          ba.asset_type === "business_interest" ||
          ba.asset_type === "insurance_asset"
      )
      .map(mapBackendAssetToExpress);
  }, [backendAssets]);

  // State for editing asset titles
  const [editingAssetId, setEditingAssetId] = useState<string | null>(null);
  const [assetTitles, setAssetTitles] = useState<Record<string, string>>({});

  // State for editing asset values
  const [editingValueId, setEditingValueId] = useState<string | null>(null);
  const [assetValues, setAssetValues] = useState<Record<string, number>>({});

  // State for tracking which assets are currently being saved
  const [savingAssetIds, setSavingAssetIds] = useState<Set<string>>(new Set());

  // State for planning mode - initialized from epf, synced when household loads
  const [planningMode, setPlanningMode] = useState<string>(() =>
    epfToPlanningMode(epf)
  );

  useEffect(() => {
    setPlanningMode(epfToPlanningMode(epf));
  }, [epf]);

  // State for asset category menu
  const [assetMenuAnchor, setAssetMenuAnchor] = useState<HTMLElement | null>(
    null
  );
  const [menuAssetId, setMenuAssetId] = useState<string | null>(null);

  // State for routing details visibility
  const [showRoutingDetails, setShowRoutingDetails] = useState(false);

  // State for payment dialog
  // const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  // const [stateEstateDoc, setStateEstateDoc] = useState<EstateDocument | null>(
  //   estateDocumentDetail || null
  // );
  // const [paywallSuccess, setPaywallSuccess] = useState(false);
  // const [paymentRequired, setPaymentRequired] = useState(false);
  // State for tracking "Enter to save" toast (session-based)
  const [enterToastCount, setEnterToastCount] = useState(0);

  // Handler for showing "Enter to save" toast
  const showEnterToast = useCallback(() => {
    if (enterToastCount < 2) {
      toast("Press Enter to save changes", {
        duration: 3000,
        icon: "⏎",
      });
      setEnterToastCount((prev) => prev + 1);
    }
  }, [enterToastCount]);

  // Handler for estate document changes
  // const handleChange = (key: string, value: any) => {
  //   setStateEstateDoc((prev) => {
  //     if (!prev) return null;
  //     return {
  //       ...prev,
  //       [key]: value,
  //     };
  //   });
  // };

  // Handler for saving an asset
  const handleSaveAsset = useCallback(
    async (assetId: string) => {
      if (!userData?.ud_id) {
        toast.error("User data not available");
        return;
      }

      // Find the asset in the list
      const asset = assets.find((a) => a.id === assetId);
      if (!asset) {
        toast.error("Asset not found");
        return;
      }

      // Get the updated title and value from state
      const updatedTitle = assetTitles[assetId] ?? asset.title;
      const updatedValue = assetValues[assetId] ?? asset.value;

      // Create updated asset
      const updatedAsset: Asset = {
        ...asset,
        title: updatedTitle,
        value: updatedValue,
      };

      // Convert to backend format
      const backendAsset = mapExpressAssetToBackend(
        updatedAsset,
        userData.ud_id
      );

      // Add to saving set
      setSavingAssetIds((prev) => new Set(prev).add(assetId));

      try {
        await handleAddOrUpdateAssets(backendAsset);
        toast.success("Asset saved");
      } catch (error) {
        console.error("Failed to save asset:", error);
        toast.error("Failed to save asset");
      } finally {
        // Remove from saving set
        setSavingAssetIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(assetId);
          return newSet;
        });
      }
    },
    [assets, assetTitles, assetValues, userData, handleAddOrUpdateAssets]
  );

  // Handler for adding new asset
  const getDefaultAssetValue = (category: AssetCategory): number => {
    const defaultValues: Record<AssetCategory, number> = {
      real_estate: 350000, // Average home value
      bank_account: 15000, // Average savings
      investment: 75000, // Average investment account
      retirement: 200000, // Average retirement account
      vehicle: 25000, // Average vehicle value
      business: 150000, // Average small business value
      life_insurance: 250000, // Average life insurance policy
    };
    return defaultValues[category] || 100000;
  };

  const handleAddAsset = async (category: AssetCategory) => {
    if (!userData?.ud_id) {
      toast.error("User data not available");
      return;
    }

    const newAssetName = getDefaultAssetName(category, assets);
    const defaultValue = getDefaultAssetValue(category);

    const newAsset: Asset = {
      id: "", // Will be generated by backend
      title: newAssetName,
      value: defaultValue,
      category: category,
      status: "included",
    };

    // Convert to backend format
    const backendAsset = mapExpressAssetToBackend(newAsset, userData.ud_id);

    try {
      // Call API to create asset
      const createdAsset = await handleAddOrUpdateAssets(backendAsset);

      if (createdAsset?.id) {

        // Set the default title in the titles state
        setAssetTitles((prev) => ({
          ...prev,
          [createdAsset.id || ""]: newAssetName,
        }));

        // Set default value in values state
        setAssetValues((prev) => ({
          ...prev,
          [createdAsset.id || ""]: defaultValue,
        }));

        // Auto-select the title for editing
        setEditingAssetId(createdAsset.id);

        // Show Enter toast when first starting to edit
        showEnterToast();
      }
    } catch (error) {
      console.error("Failed to add asset:", error);
      toast.error("Failed to add asset");
    }
  };

  const handleCloseAssetMenu = () => {
    setAssetMenuAnchor(null);
    setMenuAssetId(null);
  };

  const handleChangeAssetCategory = async (newCategory: AssetCategory) => {
    if (!menuAssetId || !userData?.ud_id) {
      handleCloseAssetMenu();
      return;
    }

    const asset = assets.find((a) => a.id === menuAssetId);
    if (!asset) {
      handleCloseAssetMenu();
      return;
    }

    const categoryLabels: Record<AssetCategory, string> = {
      real_estate: "Real Estate",
      bank_account: "Bank",
      investment: "Investment",
      retirement: "Retirement",
      vehicle: "Vehicle",
      business: "Business",
      life_insurance: "Life Insurance",
    };

    try {
      // Create updated asset with new category
      const updatedAsset: Asset = {
        ...asset,
        category: newCategory,
      };

      // Convert to backend format
      const backendAsset = mapExpressAssetToBackend(
        updatedAsset,
        userData.ud_id
      );

      // Call API to update asset
      await handleAddOrUpdateAssets(backendAsset);

      toast.success(`Changed to ${categoryLabels[newCategory]}`);
    } catch (error) {
      console.error("Failed to change asset category:", error);
      toast.error("Failed to change category");
    }

    handleCloseAssetMenu();
  };

  // Handler for planning mode changes
  const handlePlanningModeChange = (
    event: React.MouseEvent<HTMLElement>,
    newMode: string | null
  ) => {
    if (newMode !== null) {
      setPlanningMode(newMode);
    }
  };

  // Initialize selected docs state
  useEffect(() => {
    const initialSelected: Record<string, boolean> = {};
    tasks.forEach((task) => {
      initialSelected[task.id] = true; // All selected by default
    });
    setSelectedDocs(initialSelected);
  }, [tasks]);

  // Initialize asset titles
  useEffect(() => {
    const initialTitles: Record<string, string> = {};
    assets.forEach((asset) => {
      initialTitles[asset.id] = asset.title;
    });
    setAssetTitles(initialTitles);
  }, [assets]);

  // ---------------------------------------------------------------------------
  // 2. progress bar
  // ---------------------------------------------------------------------------
  // Only count selected (non-skipped) tasks in the completion calculation
  const selectedTasks = tasks.filter((t) => selectedDocs[t.id] !== false);
  const completed = selectedTasks.filter((t) => t.status === "COMPLETE").length;
  const completionPct = selectedTasks.length
    ? Math.round((completed / selectedTasks.length) * 100)
    : 0;

  // Assets page does not trigger confetti - only the main checklist does

  // ---------------------------------------------------------------------------
  // 4. render
  // ---------------------------------------------------------------------------
  // Sample data for when user has no assets
  const sampleAssets = [
    {
      id: "sample-1",
      title: "Primary Residence",
      value: 450000,
      category: "real_estate" as AssetCategory,
      status: "included" as const,
    },
    {
      id: "sample-2",
      title: "401(k) Retirement Account",
      value: 320000,
      category: "retirement" as AssetCategory,
      status: "included" as const,
    },
    {
      id: "sample-3",
      title: "Savings Account",
      value: 75000,
      category: "bank_account" as AssetCategory,
      status: "included" as const,
    },
    {
      id: "sample-4",
      title: "Investment Portfolio",
      value: 180000,
      category: "investment" as AssetCategory,
      status: "included" as const,
    },
    {
      id: "sample-5",
      title: "Life Insurance Policy",
      value: 500000,
      category: "life_insurance" as AssetCategory,
      status: "included" as const,
    },
    {
      id: "sample-6",
      title: "Family Vehicle",
      value: 35000,
      category: "vehicle" as AssetCategory,
      status: "included" as const,
    },
    {
      id: "sample-7",
      title: "Checking Account",
      value: 15000,
      category: "bank_account" as AssetCategory,
      status: "included" as const,
    },
  ];

  const displayAssets = assets.length > 0 ? assets : sampleAssets;

  // ---------------------------------------------------------------------------
  return (
    <>
      <Card
        variant="outlined"
        sx={{
          bgcolor: express ? "#FAF9F8" : "#fcfcfc",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          border: { xs: "none", sm: express ? "1px solid #e7e7e7" : "none" },
          borderRadius: { xs: 0, sm: 1 },
        }}
      >
        {/* progress bar */}
        {express && (
          <Box sx={{ px: { xs: 0, sm: 3 }, pt: { xs: 0, sm: 3 } }}>
            <LinearProgress variant="determinate" value={completionPct} />
          </Box>
        )}

        <Stack
          spacing={3}
          sx={{
            px: { xs: 0, sm: express ? 3 : 0 },
            pb: { xs: 0, sm: 3 },
            pt: { xs: express ? 3 : 0, sm: express ? 3 : 0 },
          }}
        >
          {/* Express Pack Summary Card */}
          <Card
            variant="outlined"
            sx={{ pt: 2.5, border: "1px solid #e7e7e7", borderRadius: 1 }}
          >
            <Stack spacing={2} sx={{ px: 2.5 }}>
              {express && (
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Asset Planning
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Create a personalized plan for moving each asset to your
                    beneficiaries.
                  </Typography>
                </Box>
              )}

              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  {/* Avatar Group */}
                  <AvatarGroup max={3} sx={{ justifyContent: "flex-start" }}>
                    {tasks
                      .map((t) => t.people)
                      .flat()
                      .filter(
                        (person, index, self) =>
                          index ===
                          self.findIndex((p) => p.ud_id === person.ud_id)
                      )
                      .slice(0, 3)
                      .map((person) => (
                        <Avatar
                          key={person.ud_id}
                          {...stringAvatar(person?.full_name || "")}
                        />
                      ))}
                  </AvatarGroup>

                  {/* Summary Text */}
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      {
                        tasks
                          .map((t) => t.people)
                          .flat()
                          .filter(
                            (person, index, self) =>
                              index ===
                              self.findIndex((p) => p.ud_id === person.ud_id)
                          ).length
                      }{" "}
                      people
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      • 2-5 min
                    </Typography>
                  </Stack>
                </Stack>
              </Stack>

              <Stack spacing={4} sx={{ pt: 3 }}>
                {/* Planning Mode Selector */}
                <Box sx={{ display: "flex", justifyContent: "center", my: 3 }}>
                  <ToggleButtonGroup
                    value={planningMode}
                    exclusive
                    color="primary"
                    onChange={handlePlanningModeChange}
                    aria-label="planning mode"
                    size="medium"
                    sx={{
                      backgroundColor: "primary.main",
                      p: 1.5,
                      borderRadius: "60px",
                      boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08)",
                      "@keyframes pulse": {
                        "0%": {
                          transform: "scale(1)",
                          opacity: 1,
                        },
                        "100%": {
                          transform: "scale(2)",
                          opacity: 0,
                        },
                      },
                      "& .MuiToggleButton-root": {
                        textTransform: "none",
                        fontWeight: 500,
                        px: 3,
                        py: 1,
                        color: "white",
                        border: "none",
                        "&:first-of-type": {
                          borderTopLeftRadius: "50px",
                          borderBottomLeftRadius: "50px",
                        },
                        "&:last-of-type": {
                          borderTopRightRadius: "50px",
                          borderBottomRightRadius: "50px",
                        },
                        "&.Mui-selected": {
                          fontWeight: 600,
                          backgroundColor: "white",
                          color: "black",
                          border: "none !important",
                          borderRadius: "50px !important",
                          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                          position: "relative",
                          zIndex: 1,
                          "&:hover": {
                            backgroundColor: "grey.50",
                            boxShadow: "0 3px 10px rgba(0, 0, 0, 0.2)",
                          },
                        },
                        "&:not(.Mui-selected):hover": {
                          backgroundColor: "primary.dark",
                          borderRadius: "50px",
                        },
                      },
                    }}
                  >
                    <ToggleButton value="no-plan" aria-label="no plan">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {planningMode === "no-plan" && (
                          <Box
                            sx={{
                              position: "relative",
                              width: 8,
                              height: 8,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "#f44336",
                                position: "absolute",
                                zIndex: 2,
                              }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "#f44336",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </Box>
                        )}
                        <span>No Plan</span>
                      </Stack>
                    </ToggleButton>
                    <ToggleButton value="will-plan" aria-label="will plan">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {planningMode === "will-plan" && (
                          <Box
                            sx={{
                              position: "relative",
                              width: 8,
                              height: 8,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "#ff9800",
                                position: "absolute",
                                zIndex: 2,
                              }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "#ff9800",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </Box>
                        )}
                        <span>Will Plan</span>
                      </Stack>
                    </ToggleButton>
                    <ToggleButton value="trust-plan" aria-label="trust plan">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        {planningMode === "trust-plan" && (
                          <Box
                            sx={{
                              position: "relative",
                              width: 8,
                              height: 8,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "#4caf50",
                                position: "absolute",
                                zIndex: 2,
                              }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                bgcolor: "#4caf50",
                                animation: "pulse 1.5s ease-in-out infinite",
                              }}
                            />
                          </Box>
                        )}
                        <span>Trust Plan</span>
                      </Stack>
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Stack
                  direction="row"
                  spacing={3}
                  justifyContent="center"
                  alignItems="center"
                >
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                    >
                      {assets.length === 0
                        ? "Sample Estate Value"
                        : "Total Estate Value"}
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="medium"
                      sx={{ fontSize: { xs: "0.9rem", sm: "1.25rem" } }}
                    >
                      $
                      {calculateTotalValue(
                        displayAssets,
                        assetValues
                      ).toLocaleString()}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                    >
                      Current Plan
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="medium"
                      sx={{ fontSize: { xs: "0.9rem", sm: "1.25rem" } }}
                    >
                      {planningMode === "no-plan"
                        ? "No Plan"
                        : planningMode === "will-plan"
                        ? "Will Plan"
                        : "Trust Plan"}
                    </Typography>
                  </Box>
                  <Divider orientation="vertical" flexItem />
                  <Box sx={{ textAlign: "center" }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
                    >
                      Time to Full Distribution
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="medium"
                      sx={{ fontSize: { xs: "0.9rem", sm: "1.25rem" } }}
                    >
                      {planningMode === "trust-plan"
                        ? "3 months"
                        : planningMode === "will-plan"
                        ? "9-15 months"
                        : "12-21 months"}
                    </Typography>
                  </Box>
                </Stack>

                {/* Desktop Chart */}
                <Box
                  sx={{
                    height: "400px",
                    width: "100%",
                    position: "relative",
                    display: { xs: "none", md: "block" },
                  }}
                >
                  {/* Encouragement message overlay when no assets */}
                  {assets.length === 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 20,
                        backgroundColor: "rgba(255, 255, 255, 0.98)",
                        border: "2px solid #000",
                        borderRadius: "8px",
                        padding: "24px 32px",
                        maxWidth: "500px",
                        textAlign: "center",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      <Typography
                        variant="h6"
                        fontWeight="bold"
                        sx={{ mb: 1.5 }}
                      >
                        Sample Data Shown
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Add your assets below to see how quickly your
                        beneficiaries will receive them based on different
                        estate planning strategies.
                      </Typography>
                    </Box>
                  )}

                  {/* Legend overlay */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 70,
                      right: 40,
                      zIndex: 10,
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "8px 12px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "4px",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#4caf50",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#000", fontSize: "0.75rem" }}
                      >
                        Trust Plan
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#ff9800",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#000", fontSize: "0.75rem" }}
                      >
                        Will Plan
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          bgcolor: "#f44336",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#000", fontSize: "0.75rem" }}
                      >
                        No Plan
                      </Typography>
                    </Box>
                  </Box>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={calculateTimelineData(
                        displayAssets,
                        assetValues,
                        planningMode
                      )}
                      margin={{ top: 20, right: 30, left: 60, bottom: 30 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorNoPlan"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#f44336"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#f44336"
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorWillPlan"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#ff9800"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#ff9800"
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorTrustPlan"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#4caf50"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#4caf50"
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                      <XAxis
                        dataKey="month"
                        type="number"
                        domain={[0, 21]}
                        label={{
                          value:
                            "How quickly beneficiaries receive funds (months after death)",
                          position: "insideBottom",
                          offset: -20,
                          style: { textAnchor: "middle" },
                        }}
                        ticks={[0, 3, 6, 9, 12, 15, 18, 21]}
                        tickFormatter={(value) => value.toString()}
                        tick={{ dy: 6 }}
                      />

                      <YAxis
                        domain={[0, "dataMax"]}
                        tick={{ dx: -6 }}
                        tickFormatter={(value) => formatCurrency(value)}
                      />

                      <RechartsTooltip
                        content={(props: any) => {
                          const { active, payload, label } = props;
                          if (active && payload && payload.length) {
                            // Sort payload to display in the order: Trust Plan, Will Plan, No Plan
                            const sortedPayload = [...payload].sort((a, b) => {
                              const order = [
                                "Trust Plan",
                                "Will Plan",
                                "No Plan",
                              ];
                              return (
                                order.indexOf(a.dataKey) -
                                order.indexOf(b.dataKey)
                              );
                            });

                            return (
                              <Box
                                sx={{
                                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  padding: "8px 12px",
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: "bold", mb: 0.5 }}
                                >
                                  Month {label}
                                </Typography>
                                {sortedPayload.map(
                                  (entry: any, index: number) => {
                                    let dotColor = "#000";
                                    if (entry.dataKey === "No Plan")
                                      dotColor = "#f44336";
                                    if (entry.dataKey === "Will Plan")
                                      dotColor = "#ff9800";
                                    if (entry.dataKey === "Trust Plan")
                                      dotColor = "#4caf50";

                                    return (
                                      <Box
                                        key={index}
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            bgcolor: dotColor,
                                          }}
                                        />
                                        <Typography
                                          variant="caption"
                                          sx={{
                                            color: "#000",
                                            fontSize: "0.75rem",
                                          }}
                                        >
                                          {entry.dataKey}: $
                                          {entry.value.toLocaleString()}
                                        </Typography>
                                      </Box>
                                    );
                                  }
                                )}
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />

                      {/* Area fills */}
                      {planningMode === "no-plan" && (
                        <Area
                          type="monotone"
                          dataKey="No Plan"
                          stroke="#f44336"
                          strokeWidth={3}
                          fill="url(#colorNoPlan)"
                          animationDuration={1000}
                        />
                      )}

                      {planningMode === "will-plan" && (
                        <Area
                          type="monotone"
                          dataKey="Will Plan"
                          stroke="#ff9800"
                          strokeWidth={3}
                          fill="url(#colorWillPlan)"
                          animationDuration={1000}
                        />
                      )}

                      {planningMode === "trust-plan" && (
                        <Area
                          type="monotone"
                          dataKey="Trust Plan"
                          stroke="#4caf50"
                          strokeWidth={3}
                          fill="url(#colorTrustPlan)"
                          animationDuration={1000}
                        />
                      )}

                      {/* Comparison lines (thinner, dashed) */}
                      {planningMode !== "no-plan" && (
                        <Line
                          type="monotone"
                          dataKey="No Plan"
                          stroke="#f44336"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={false}
                          opacity={0.5}
                        />
                      )}

                      {planningMode !== "will-plan" && (
                        <Line
                          type="monotone"
                          dataKey="Will Plan"
                          stroke="#ff9800"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={false}
                          opacity={0.5}
                        />
                      )}

                      {planningMode !== "trust-plan" && (
                        <Line
                          type="monotone"
                          dataKey="Trust Plan"
                          stroke="#4caf50"
                          strokeWidth={2}
                          strokeDasharray="5 5"
                          dot={false}
                          opacity={0.5}
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>

                {/* Mobile Chart */}
                <Box
                  sx={{
                    height: "300px",
                    width: "100%",
                    position: "relative",
                    display: { xs: "block", md: "none" },
                  }}
                >
                  {/* Encouragement message overlay when no assets */}
                  {assets.length === 0 && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        zIndex: 20,
                        backgroundColor: "rgba(255, 255, 255, 0.98)",
                        border: "2px solid #000",
                        borderRadius: "8px",
                        padding: "16px 20px",
                        maxWidth: "90%",
                        textAlign: "center",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.15)",
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        fontWeight="bold"
                        sx={{ mb: 1 }}
                      >
                        Sample Data Shown
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Add your assets below to see your personalized timeline.
                      </Typography>
                    </Box>
                  )}

                  {/* Compact Legend overlay for mobile */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 50,
                      right: 8,
                      zIndex: 10,
                      backgroundColor: "rgba(255, 255, 255, 0.95)",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                      padding: "4px 6px",
                      display: "flex",
                      flexDirection: "column",
                      gap: "2px",
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "#4caf50",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#000", fontSize: "0.65rem" }}
                      >
                        Trust
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "#ff9800",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#000", fontSize: "0.65rem" }}
                      >
                        Will
                      </Typography>
                    </Box>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: "50%",
                          bgcolor: "#f44336",
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{ color: "#000", fontSize: "0.65rem" }}
                      >
                        No Plan
                      </Typography>
                    </Box>
                  </Box>

                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={calculateTimelineData(
                        displayAssets,
                        assetValues,
                        planningMode
                      )}
                      margin={{ top: 10, right: 0, left: -20, bottom: 40 }}
                    >
                      <defs>
                        <linearGradient
                          id="colorNoPlanMobile"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#f44336"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#f44336"
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorWillPlanMobile"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#ff9800"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#ff9800"
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                        <linearGradient
                          id="colorTrustPlanMobile"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor="#4caf50"
                            stopOpacity={0.8}
                          />
                          <stop
                            offset="95%"
                            stopColor="#4caf50"
                            stopOpacity={0.1}
                          />
                        </linearGradient>
                      </defs>

                      <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />

                      <XAxis
                        dataKey="month"
                        type="number"
                        domain={[0, 21]}
                        label={{
                          value: "Months",
                          position: "insideBottom",
                          offset: -5,
                          style: { fontSize: "0.75rem" },
                        }}
                        ticks={[0, 6, 12, 18]}
                        tickFormatter={(value) => value.toString()}
                        tick={{ fontSize: 10 }}
                      />

                      <YAxis
                        domain={[0, "dataMax"]}
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => formatCurrency(value)}
                      />

                      <RechartsTooltip
                        content={(props: any) => {
                          const { active, payload, label } = props;
                          if (active && payload && payload.length) {
                            const currentMode =
                              planningMode === "no-plan"
                                ? "No Plan"
                                : planningMode === "will-plan"
                                ? "Will Plan"
                                : "Trust Plan";
                            const entry = payload.find(
                              (p: any) => p.dataKey === currentMode
                            );

                            if (!entry) return null;

                            let dotColor = "#000";
                            if (currentMode === "No Plan") dotColor = "#f44336";
                            if (currentMode === "Will Plan")
                              dotColor = "#ff9800";
                            if (currentMode === "Trust Plan")
                              dotColor = "#4caf50";

                            return (
                              <Box
                                sx={{
                                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  padding: "6px 10px",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: "bold",
                                    display: "block",
                                    mb: 0.5,
                                  }}
                                >
                                  Month {label}
                                </Typography>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: "50%",
                                      bgcolor: dotColor,
                                    }}
                                  />
                                  <Typography variant="caption">
                                    ${entry.value.toLocaleString()}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />

                      {/* Show only the selected plan's area */}
                      {planningMode === "no-plan" && (
                        <Area
                          type="monotone"
                          dataKey="No Plan"
                          stroke="#f44336"
                          strokeWidth={2}
                          fill="url(#colorNoPlanMobile)"
                          animationDuration={1000}
                        />
                      )}

                      {planningMode === "will-plan" && (
                        <Area
                          type="monotone"
                          dataKey="Will Plan"
                          stroke="#ff9800"
                          strokeWidth={2}
                          fill="url(#colorWillPlanMobile)"
                          animationDuration={1000}
                        />
                      )}

                      {planningMode === "trust-plan" && (
                        <Area
                          type="monotone"
                          dataKey="Trust Plan"
                          stroke="#4caf50"
                          strokeWidth={2}
                          fill="url(#colorTrustPlanMobile)"
                          animationDuration={1000}
                        />
                      )}

                      {/* Comparison lines (thinner, dashed) */}
                      {planningMode !== "no-plan" && (
                        <Line
                          type="monotone"
                          dataKey="No Plan"
                          stroke="#f44336"
                          strokeWidth={1.5}
                          strokeDasharray="3 3"
                          dot={false}
                          opacity={0.5}
                        />
                      )}

                      {planningMode !== "will-plan" && (
                        <Line
                          type="monotone"
                          dataKey="Will Plan"
                          stroke="#ff9800"
                          strokeWidth={1.5}
                          strokeDasharray="3 3"
                          dot={false}
                          opacity={0.5}
                        />
                      )}

                      {planningMode !== "trust-plan" && (
                        <Line
                          type="monotone"
                          dataKey="Trust Plan"
                          stroke="#4caf50"
                          strokeWidth={1.5}
                          strokeDasharray="3 3"
                          dot={false}
                          opacity={0.5}
                        />
                      )}
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>

                {/* Asset Routing Show Details Link */}
                <Box
                  sx={{
                    px: 1,
                    display: "flex",
                    justifyContent: "center",
                    pb: showRoutingDetails ? 0 : 2,
                  }}
                >
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => setShowRoutingDetails(!showRoutingDetails)}
                    endIcon={
                      <ExpandMoreIcon
                        sx={{
                          fontSize: 18,
                          transition: "transform 0.2s",
                          transform: showRoutingDetails
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      />
                    }
                    sx={{
                      color: "text.secondary",
                      textTransform: "none",
                      fontSize: "0.875rem",
                      padding: "2px 4px",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                    }}
                  >
                    {showRoutingDetails ? "Hide details" : "Show details"}
                  </Button>
                </Box>

                {/* Asset Routing Details - Conditionally Rendered */}
                {showRoutingDetails && (
                  <Box sx={{ mt: 1 }}>
                    {/* Routing Options Grid */}
                    <Grid container>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ px: 2, py: 2, height: "100%" }}>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              Direct
                            </Typography>
                            <Typography
                              variant="h5"
                              color="primary"
                              fontWeight="bold"
                              sx={{ mt: 1, mb: 0.5 }}
                            >
                              $
                              {getRoutingBreakdown(
                                displayAssets,
                                assetValues,
                                planningMode
                              ).direct.toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Immediate transfer
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {(
                                (getRoutingBreakdown(
                                  displayAssets,
                                  assetValues,
                                  planningMode
                                ).direct /
                                  calculateTotalValue(
                                    displayAssets,
                                    assetValues
                                  )) *
                                100
                              ).toFixed(0)}
                              % of estate
                            </Typography>
                          </Box>

                          {/* Assets in this category */}
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: "1px solid rgba(0,0,0,0.08)",
                            }}
                          >
                            {getAssetsByRouting(
                              displayAssets,
                              assetValues,
                              assetTitles,
                              planningMode
                            ).direct.length > 0 ? (
                              getAssetsByRouting(
                                displayAssets,
                                assetValues,
                                assetTitles,
                                planningMode
                              ).direct.map((asset) => {
                                // Choose icon based on asset category
                                let AssetIcon =
                                  AccountBalanceWalletOutlinedIcon;
                                if (asset.category === "real_estate")
                                  AssetIcon = HomeOutlinedIcon;
                                if (asset.category === "vehicle")
                                  AssetIcon = DirectionsCarOutlinedIcon;
                                if (asset.category === "bank_account")
                                  AssetIcon = SavingsOutlinedIcon;
                                if (asset.category === "investment")
                                  AssetIcon = EqualizerOutlinedIcon;
                                if (asset.category === "retirement")
                                  AssetIcon = BeachAccessOutlinedIcon;
                                if (asset.category === "business")
                                  AssetIcon = BusinessOutlinedIcon;
                                if (asset.category === "life_insurance")
                                  AssetIcon = GppGoodOutlinedIcon;

                                return (
                                  <Box
                                    key={asset.id}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <AssetIcon
                                      sx={{
                                        fontSize: "14px",
                                        mr: 1,
                                        color: "text.secondary",
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {assetTitles[asset.id] || asset.title}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      $
                                      {(assetValues[asset.id] !== undefined
                                        ? assetValues[asset.id]
                                        : asset.value
                                      ).toLocaleString()}
                                    </Typography>
                                  </Box>
                                );
                              })
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", textAlign: "center" }}
                              >
                                None
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        sx={{
                          borderLeft: { sm: "1px solid rgba(0,0,0,0.1)" },
                          borderRight: { sm: "1px solid rgba(0,0,0,0.1)" },
                        }}
                      >
                        <Box
                          sx={{
                            px: 2,
                            py: 2,
                            height: "100%",
                          }}
                        >
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              Trust
                            </Typography>
                            <Typography
                              variant="h5"
                              color="primary"
                              fontWeight="bold"
                              sx={{ mt: 1, mb: 0.5 }}
                            >
                              $
                              {getRoutingBreakdown(
                                displayAssets,
                                assetValues,
                                planningMode
                              ).trust.toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Via trust administration
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {(
                                (getRoutingBreakdown(
                                  displayAssets,
                                  assetValues,
                                  planningMode
                                ).trust /
                                  calculateTotalValue(
                                    displayAssets,
                                    assetValues
                                  )) *
                                100
                              ).toFixed(0)}
                              % of estate
                            </Typography>
                          </Box>

                          {/* Assets in this category */}
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: "1px solid rgba(0,0,0,0.08)",
                            }}
                          >
                            {getAssetsByRouting(
                              displayAssets,
                              assetValues,
                              assetTitles,
                              planningMode
                            ).trust.length > 0 ? (
                              getAssetsByRouting(
                                displayAssets,
                                assetValues,
                                assetTitles,
                                planningMode
                              ).trust.map((asset) => {
                                // Choose icon based on asset category
                                let AssetIcon =
                                  AccountBalanceWalletOutlinedIcon;
                                if (asset.category === "real_estate")
                                  AssetIcon = HomeOutlinedIcon;
                                if (asset.category === "vehicle")
                                  AssetIcon = DirectionsCarOutlinedIcon;
                                if (asset.category === "bank_account")
                                  AssetIcon = SavingsOutlinedIcon;
                                if (asset.category === "investment")
                                  AssetIcon = EqualizerOutlinedIcon;
                                if (asset.category === "retirement")
                                  AssetIcon = BeachAccessOutlinedIcon;
                                if (asset.category === "business")
                                  AssetIcon = BusinessOutlinedIcon;
                                if (asset.category === "life_insurance")
                                  AssetIcon = GppGoodOutlinedIcon;

                                return (
                                  <Box
                                    key={asset.id}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <AssetIcon
                                      sx={{
                                        fontSize: "14px",
                                        mr: 1,
                                        color: "text.secondary",
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {assetTitles[asset.id] || asset.title}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      $
                                      {(assetValues[asset.id] !== undefined
                                        ? assetValues[asset.id]
                                        : asset.value
                                      ).toLocaleString()}
                                    </Typography>
                                  </Box>
                                );
                              })
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", textAlign: "center" }}
                              >
                                None
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box sx={{ px: 2, py: 2, height: "100%" }}>
                          <Box sx={{ textAlign: "center" }}>
                            <Typography variant="subtitle1" fontWeight="medium">
                              Probate
                            </Typography>
                            <Typography
                              variant="h5"
                              color="primary"
                              fontWeight="bold"
                              sx={{ mt: 1, mb: 0.5 }}
                            >
                              $
                              {getRoutingBreakdown(
                                displayAssets,
                                assetValues,
                                planningMode
                              ).probate.toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Court supervised
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ mt: 0.5, display: "block" }}
                            >
                              {(
                                (getRoutingBreakdown(
                                  displayAssets,
                                  assetValues,
                                  planningMode
                                ).probate /
                                  calculateTotalValue(
                                    displayAssets,
                                    assetValues
                                  )) *
                                100
                              ).toFixed(0)}
                              % of estate
                            </Typography>
                          </Box>

                          {/* Assets in this category */}
                          <Box
                            sx={{
                              mt: 2,
                              pt: 2,
                              borderTop: "1px solid rgba(0,0,0,0.08)",
                            }}
                          >
                            {getAssetsByRouting(
                              displayAssets,
                              assetValues,
                              assetTitles,
                              planningMode
                            ).probate.length > 0 ? (
                              getAssetsByRouting(
                                displayAssets,
                                assetValues,
                                assetTitles,
                                planningMode
                              ).probate.map((asset) => {
                                // Choose icon based on asset category
                                let AssetIcon =
                                  AccountBalanceWalletOutlinedIcon;
                                if (asset.category === "real_estate")
                                  AssetIcon = HomeOutlinedIcon;
                                if (asset.category === "vehicle")
                                  AssetIcon = DirectionsCarOutlinedIcon;
                                if (asset.category === "bank_account")
                                  AssetIcon = SavingsOutlinedIcon;
                                if (asset.category === "investment")
                                  AssetIcon = EqualizerOutlinedIcon;
                                if (asset.category === "retirement")
                                  AssetIcon = BeachAccessOutlinedIcon;
                                if (asset.category === "business")
                                  AssetIcon = BusinessOutlinedIcon;
                                if (asset.category === "life_insurance")
                                  AssetIcon = GppGoodOutlinedIcon;

                                return (
                                  <Box
                                    key={asset.id}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      mb: 1,
                                    }}
                                  >
                                    <AssetIcon
                                      sx={{
                                        fontSize: "14px",
                                        mr: 1,
                                        color: "text.secondary",
                                      }}
                                    />
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        flex: 1,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {assetTitles[asset.id] || asset.title}
                                    </Typography>
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      $
                                      {(assetValues[asset.id] !== undefined
                                        ? assetValues[asset.id]
                                        : asset.value
                                      ).toLocaleString()}
                                    </Typography>
                                  </Box>
                                );
                              })
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block", textAlign: "center" }}
                              >
                                None
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>

                    {/* Explanatory Note */}
                    <Box
                      sx={{
                        mt: 2,
                        p: 2,
                        borderRadius: 1,
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 1.5,
                      }}
                    >
                      <InfoOutlinedIcon
                        sx={{
                          fontSize: 18,
                          color: "text.secondary",
                          mt: 0.25,
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ lineHeight: 1.6 }}
                      >
                        <strong>Note:</strong> The charts and timelines shown
                        above are for illustration purposes only. Actual
                        transfer of assets will depend on proper beneficiary
                        designations, completion of trust funding, and your
                        state's specific probate laws and court timelines.
                        Consult with an attorney for guidance specific to your
                        situation.
                      </Typography>
                    </Box>
                  </Box>
                )}
              </Stack>
            </Stack>
          </Card>

          {/* Assets by Category */}
          {(() => {
            // Group assets by category
            const assetsByCategory: Record<AssetCategory, Asset[]> = {
              real_estate: [],
              bank_account: [],
              investment: [],
              retirement: [],
              vehicle: [],
              business: [],
              life_insurance: [],
            };

            assets.forEach((asset) => {
              assetsByCategory[asset.category].push(asset);
            });

            // Define category order and labels
            const categoryOrder: AssetCategory[] = [
              "real_estate",
              "bank_account",
              "investment",
              "retirement",
              "vehicle",
              "business",
              "life_insurance",
            ];

            const categoryLabels: Record<AssetCategory, string> = {
              real_estate: "Real Estate",
              bank_account: "Bank Accounts",
              investment: "Investments",
              retirement: "Retirement Accounts",
              vehicle: "Vehicles",
              business: "Business Interests",
              life_insurance: "Life Insurance",
            };

            // Render individual Cards for each category
            return categoryOrder.map((category) => {
              const categoryAssets = assetsByCategory[category];

              // Get category icon
              let CategoryIcon = AccountBalanceWalletOutlinedIcon;
              if (category === "real_estate") CategoryIcon = HomeOutlinedIcon;
              if (category === "vehicle")
                CategoryIcon = DirectionsCarOutlinedIcon;
              if (category === "bank_account")
                CategoryIcon = SavingsOutlinedIcon;
              if (category === "investment")
                CategoryIcon = EqualizerOutlinedIcon;
              if (category === "retirement")
                CategoryIcon = BeachAccessOutlinedIcon;
              if (category === "business") CategoryIcon = BusinessOutlinedIcon;
              if (category === "life_insurance")
                CategoryIcon = GppGoodOutlinedIcon;

              return (
                <Card
                  key={category}
                  variant="outlined"
                  sx={{ mb: 2, border: "1px solid #e7e7e7", borderRadius: 1 }}
                >
                  {/* Category Header */}
                  <Box
                    sx={{
                      py: 1.5,
                      px: 2.5,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Avatar
                        sx={{
                          bgcolor: getCategoryColors(category).dotColor,
                          width: 20,
                          height: 20,
                        }}
                      >
                        <CategoryIcon
                          sx={{ fontSize: "12px", color: "white" }}
                        />
                      </Avatar>
                      <Typography variant="body2">
                        {categoryLabels[category]}
                      </Typography>
                    </Stack>
                    <Tooltip title={`Add ${categoryLabels[category]}`}>
                      <IconButton onClick={() => handleAddAsset(category)}>
                        <AddRoundedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Divider />

                  {/* Asset List */}
                  <List disablePadding sx={{ px: 0, py: 0 }}>
                    {categoryAssets.length === 0 ? (
                      <ListItem
                        sx={{
                          py: 1.5,
                          px: 3,
                          pl: 4,
                        }}
                      >
                        <Button
                          startIcon={<AddIcon />}
                          onClick={() => handleAddAsset(category)}
                          size="small"
                          sx={{
                            textTransform: "none",
                            color: "text.secondary",
                            minWidth: "auto",
                            fontWeight: 400,
                            "&:hover": {
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          Add {categoryLabels[category]}
                        </Button>
                      </ListItem>
                    ) : (
                      categoryAssets.map((asset) => {
                        // Choose icon based on asset category
                        let AssetIcon = AccountBalanceWalletOutlinedIcon;
                        if (asset.category === "real_estate")
                          AssetIcon = HomeOutlinedIcon;
                        if (asset.category === "vehicle")
                          AssetIcon = DirectionsCarOutlinedIcon;
                        if (asset.category === "bank_account")
                          AssetIcon = SavingsOutlinedIcon;
                        if (asset.category === "investment")
                          AssetIcon = EqualizerOutlinedIcon;
                        if (asset.category === "retirement")
                          AssetIcon = BeachAccessOutlinedIcon;
                        if (asset.category === "business")
                          AssetIcon = BusinessOutlinedIcon;
                        if (asset.category === "life_insurance")
                          AssetIcon = GppGoodOutlinedIcon;

                        return (
                          <ListItem
                            key={asset.id}
                            sx={{
                              py: 1.5,
                              px: { xs: 2, sm: 3 },
                              pl: { xs: 2, sm: 4 },
                              cursor: "default",
                              "&:hover": {
                                bgcolor: "action.hover",
                                borderRadius: 1,
                              },
                            }}
                          >
                            {/* Icon */}
                            <ListItemAvatar
                              sx={{
                                minWidth: 36,
                                display: { xs: "none", sm: "block" },
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "white",
                                  color: "grey.800",
                                  width: 24,
                                  height: 24,
                                }}
                              >
                                <AssetIcon
                                  fontSize="small"
                                  sx={{ fontSize: "1.2em" }}
                                />
                              </Avatar>
                            </ListItemAvatar>

                            {/* Show saving indicator if needed */}
                            {savingAssetIds.has(asset.id) && (
                              <Box sx={{ mr: 2 }}>
                                <CircularProgress size={16} />
                              </Box>
                            )}

                            {/* Title - Editable inline */}
                            <ListItemText
                              primary={
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  spacing={1}
                                >
                                  {editingAssetId === asset.id ? (
                                    <TextField
                                      value={assetTitles[asset.id] || ""}
                                      onChange={(e) => {
                                        setAssetTitles((prev) => ({
                                          ...prev,
                                          [asset.id]: e.target.value,
                                        }));
                                      }}
                                      onBlur={() => {
                                        setEditingAssetId(null);
                                        handleSaveAsset(asset.id);
                                      }}
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                          setEditingAssetId(null);
                                          handleSaveAsset(asset.id);
                                          setEnterToastCount(2);
                                        }
                                        if (e.key === "Escape") {
                                          setAssetTitles((prev) => ({
                                            ...prev,
                                            [asset.id]: asset.title,
                                          }));
                                          setEditingAssetId(null);
                                        }
                                      }}
                                      size="small"
                                      autoFocus
                                      fullWidth
                                      variant="standard"
                                      sx={{
                                        "& .MuiInput-underline:before": {
                                          borderBottom:
                                            "1px solid rgba(0, 0, 0, 0.1)",
                                        },
                                      }}
                                    />
                                  ) : (
                                    <Typography
                                      variant="subtitle2"
                                      onClick={() => {
                                        setEditingAssetId(asset.id);
                                        showEnterToast();
                                      }}
                                      sx={{
                                        cursor: "text",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        "&:hover": {
                                          textDecoration: "underline",
                                          textDecorationStyle: "dotted",
                                        },
                                      }}
                                    >
                                      {assetTitles[asset.id] || asset.title}
                                    </Typography>
                                  )}
                                </Stack>
                              }
                            />

                            {/* Value - Editable inline */}
                            <Box sx={{ mr: 1 }}>
                              {editingValueId === asset.id ? (
                                <TextField
                                  value={
                                    assetValues[asset.id] !== undefined
                                      ? assetValues[asset.id]
                                      : asset.value
                                  }
                                  onChange={(e) => {
                                    const value =
                                      parseFloat(
                                        e.target.value.replace(/[^0-9.-]/g, "")
                                      ) || 0;
                                    setAssetValues((prev) => ({
                                      ...prev,
                                      [asset.id]: value,
                                    }));
                                  }}
                                  onBlur={() => {
                                    setEditingValueId(null);
                                    handleSaveAsset(asset.id);
                                  }}
                                  onFocus={(e) => {
                                    e.target.select();
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") {
                                      setEditingValueId(null);
                                      handleSaveAsset(asset.id);
                                      setEnterToastCount(2);
                                    }
                                    if (e.key === "Escape") {
                                      setAssetValues((prev) => ({
                                        ...prev,
                                        [asset.id]: asset.value,
                                      }));
                                      setEditingValueId(null);
                                    }
                                  }}
                                  size="small"
                                  autoFocus
                                  variant="standard"
                                  type="text"
                                  InputProps={{
                                    startAdornment: "$",
                                  }}
                                  sx={{
                                    width: 120,
                                    "& .MuiInput-underline:before": {
                                      borderBottom:
                                        "1px solid rgba(0, 0, 0, 0.1)",
                                    },
                                    "& input[type=text]": {
                                      textAlign: "right",
                                    },
                                  }}
                                />
                              ) : (
                                (() => {
                                  const currentValue =
                                    assetValues[asset.id] !== undefined
                                      ? assetValues[asset.id]
                                      : asset.value;
                                  const defaultValue = getDefaultAssetValue(
                                    asset.category
                                  );
                                  const isDefaultValue =
                                    currentValue === defaultValue;

                                  const chipElement = (
                                    <Chip
                                      label={`$${currentValue.toLocaleString()}`}
                                      size="small"
                                      icon={
                                        <Box
                                          sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            bgcolor: isDefaultValue
                                              ? "grey.500"
                                              : "success.main",
                                            ml: 0.5,
                                          }}
                                        />
                                      }
                                      onClick={() => {
                                        setEditingValueId(asset.id);
                                        showEnterToast();
                                      }}
                                      sx={{
                                        bgcolor: isDefaultValue
                                          ? "#f5f5f5"
                                          : "#e8f5e9",
                                        color: "black",
                                        cursor: "text",
                                        fontSize: {
                                          xs: "0.7rem",
                                          sm: "0.8125rem",
                                        },
                                        height: { xs: "20px", sm: "24px" },
                                        "&:hover": {
                                          bgcolor: isDefaultValue
                                            ? "#f5f5f5"
                                            : "#e8f5e9",
                                          opacity: 0.8,
                                        },
                                        "& .MuiChip-icon": {
                                          marginLeft: "8px",
                                          marginRight: "-2px",
                                        },
                                        "& .MuiChip-label": {
                                          px: { xs: 1, sm: 1.5 },
                                        },
                                      }}
                                    />
                                  );

                                  return (
                                    <Tooltip
                                      title={
                                        isDefaultValue
                                          ? "Click to edit the default value"
                                          : "Custom value set"
                                      }
                                    >
                                      {chipElement}
                                    </Tooltip>
                                  );
                                })()
                              )}
                            </Box>

                            {/* Delete or Save button */}
                            {editingAssetId === asset.id ||
                            editingValueId === asset.id ? (
                              <Tooltip title="Save">
                                <IconButton
                                  size="small"
                                  color="inherit"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setEditingAssetId(null);
                                    setEditingValueId(null);
                                    handleSaveAsset(asset.id);
                                  }}
                                  sx={{ minWidth: "auto" }}
                                >
                                  <CheckOutlinedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="Remove asset">
                                <IconButton
                                  size="small"
                                  color="inherit"
                                  onClick={async (e) => {
                                    e.stopPropagation();
                                    const assetName =
                                      assetTitles[asset.id] || asset.title;

                                    try {
                                      await handleDeleteAssets(asset.id);
                                      toast.success(`Removed ${assetName}`);
                                    } catch (error) {
                                      console.error(
                                        "Failed to remove asset:",
                                        error
                                      );
                                      toast.error("Failed to remove asset");
                                    }
                                  }}
                                  sx={{ minWidth: "auto" }}
                                >
                                  <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </ListItem>
                        );
                      })
                    )}
                  </List>
                </Card>
              );
            });
          })()}

          {/* Asset Category Menu */}
          <Menu
            anchorEl={assetMenuAnchor}
            open={Boolean(assetMenuAnchor)}
            onClose={handleCloseAssetMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <MenuItem
              onClick={() => handleChangeAssetCategory("real_estate")}
              selected={
                menuAssetId
                  ? assets.find((a) => a.id === menuAssetId)?.category ===
                    "real_estate"
                  : false
              }
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <HomeOutlinedIcon fontSize="small" />
              </ListItemAvatar>
              <ListItemText primary="Real Estate" />
            </MenuItem>
            <MenuItem
              onClick={() => handleChangeAssetCategory("bank_account")}
              selected={
                menuAssetId
                  ? assets.find((a) => a.id === menuAssetId)?.category ===
                    "bank_account"
                  : false
              }
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <AccountBalanceOutlinedIcon fontSize="small" />
              </ListItemAvatar>
              <ListItemText primary="Bank Account" />
            </MenuItem>
            <MenuItem
              onClick={() => handleChangeAssetCategory("investment")}
              selected={
                menuAssetId
                  ? assets.find((a) => a.id === menuAssetId)?.category ===
                    "investment"
                  : false
              }
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <SavingsOutlinedIcon fontSize="small" />
              </ListItemAvatar>
              <ListItemText primary="Investment" />
            </MenuItem>
            <MenuItem
              onClick={() => handleChangeAssetCategory("retirement")}
              selected={
                menuAssetId
                  ? assets.find((a) => a.id === menuAssetId)?.category ===
                    "retirement"
                  : false
              }
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <AccountBalanceWalletOutlinedIcon fontSize="small" />
              </ListItemAvatar>
              <ListItemText primary="Retirement" />
            </MenuItem>
            <MenuItem
              onClick={() => handleChangeAssetCategory("vehicle")}
              selected={
                menuAssetId
                  ? assets.find((a) => a.id === menuAssetId)?.category ===
                    "vehicle"
                  : false
              }
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <DirectionsCarOutlinedIcon fontSize="small" />
              </ListItemAvatar>
              <ListItemText primary="Vehicle" />
            </MenuItem>
            <MenuItem
              onClick={() => handleChangeAssetCategory("business")}
              selected={
                menuAssetId
                  ? assets.find((a) => a.id === menuAssetId)?.category ===
                    "business"
                  : false
              }
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <BusinessOutlinedIcon fontSize="small" />
              </ListItemAvatar>
              <ListItemText primary="Business" />
            </MenuItem>
            <MenuItem
              onClick={() => handleChangeAssetCategory("life_insurance")}
              selected={
                menuAssetId
                  ? assets.find((a) => a.id === menuAssetId)?.category ===
                    "life_insurance"
                  : false
              }
            >
              <ListItemAvatar sx={{ minWidth: 36 }}>
                <SavingsOutlinedIcon fontSize="small" />
              </ListItemAvatar>
              <ListItemText primary="Life Insurance" />
            </MenuItem>
          </Menu>
        </Stack>
      </Card>

      {/* Payment Dialog */}
      {/* <ExpressPaymentDialog
        open={paymentDialogOpen}
        onClose={() => setPaymentDialogOpen(false)}
        stateEstateDoc={stateEstateDoc}
        ud_id={effectiveUdId}
        product={stateEstateDoc?.type || null}
        // Stripe props from household context
        paymentRequiredData={paymentRequiredData}
        paymentRequiredLoading={paymentRequiredLoading}
        paymentsSaving={paymentsSaving}
        currentPaymentIntent={currentPaymentIntent}
        handleCreateOrUpdatePaymentIntent={handleCreateOrUpdatePaymentIntent}
        handleClearPaymentIntent={handleClearPaymentIntent}
        handleGetPaymentRequired={handleGetPaymentRequired}
        createSetupIntent={createSetupIntent}
        createTrialSubscription={createTrialSubscription}
        // Document-specific props
        handleEstateDocPartialUpdate={handleEstateDocPartialUpdate}
        handleChange={handleChange}
        setPaywallSuccess={setPaywallSuccess}
        setPaymentRequired={setPaymentRequired}
      /> */}

    </>
  );
};

export { HouseholdAssetsExpress };
